import cx from 'classnames2';
import React, { forwardRef } from 'react';

import { AppSwitcher } from '@opengov/components-app-switcher';

import {
  Illustration,
  IllustrationTypes,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip
} from '@opengov/component-library';

import styles from './NavigationBar.module.scss';

export const ProductSwitcher = () => (
  <Popover id="product-switcher" placement="right-end" closeOnBlur={true} returnFocusOnClose={false}>
    <PopoverTrigger>
      <button className={styles.productSwitcherContainer} id="product-switcher" tabIndex={0}>
        <Illustration className={styles.productSwitcher} illustration="product-switcher" />
        <span className={styles.productSwitcherLabel}>OpenGov Suite</span>
      </button>
    </PopoverTrigger>
    <PopoverContent className={styles.productSwitcherMenu}>
      <span className={styles.label}>Switch product</span>
      <ul>
        <li>Budget & Reporting</li>
        <li>Citizen Services</li>
        <li>Open Data</li>
        <li>OpenGov Financials</li>
        <li>Open Town Hall</li>
      </ul>
    </PopoverContent>
  </Popover>
);

interface INavigationBarUserMenuPopover {
  currentEntity: string;
  session: any;
}

export const NavigationBarUserMenuPopover: React.FC<INavigationBarUserMenuPopover> = ({ currentEntity, session }) => (
  <Popover id="user-menu" placement="right-end" closeOnBlur={true} returnFocusOnClose={false}>
    <PopoverTrigger>
      <button className={styles.user}>
        <div
          className={styles.userIcon}
          style={{
            backgroundImage: `url(${session.avatar})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover'
          }}
        >
          {session.avatar ? null : `${session.first_name[0]}${session.last_name[0]}`}
        </div>
      </button>
    </PopoverTrigger>
    <PopoverContent className={styles.userMenu}>
      <span className={styles.label}>{session.email}</span>
      <ul>
        <li>
          <a href={`/access/${currentEntity}/users/${session.id}`}>Profile</a>
        </li>
        <li>
          {/* tslint:disable-next-line:react-a11y-anchors */}
          <a href="/access/logout">Logout</a>
        </li>
      </ul>
    </PopoverContent>
  </Popover>
);

export interface INavigationBarAppLinkProps {
  isActive?: boolean;
  name?: string;
  illustration?: string;
  href?: string;
}

// tslint:disable-next-line:variable-name
const _NavigationBarAppLink: React.FC<INavigationBarAppLinkProps> = (
  { isActive, name, illustration, href, ...rest },
  ref
) => (
  <a ref={ref} href={href} className={cx(styles.app, isActive && [styles.activeSwitcher, styles.active])} {...rest}>
    <Illustration
      className={styles.appIcon}
      illustration={`${illustration}-inactive` as IllustrationTypes}
      ariaHidden={true}
    />
    <Illustration
      className={cx(styles.appIcon, styles.active)}
      illustration={`${illustration}-active` as IllustrationTypes}
      ariaHidden={true}
    />
    {name}
  </a>
);

// hack because zeus blows up when forwardRef is used like it should be
export const NavigationBarAppLink = (() => forwardRef(_NavigationBarAppLink))();

export const NavigationBarBottom = ({ children }) => <div className={styles.bottom}>{children}</div>;

export interface INavigationBarBottomLink {
  href?: string;
  id?: string;
  isExternal?: boolean;
}

export const NavigationBarBottomLink: React.FC<INavigationBarBottomLink> = ({ href, id, children, isExternal }) => (
  <a href={href} id={id ? id : null} className={styles.link} tabIndex={0} target={isExternal ? '_blank' : null}>
    {children}
  </a>
);

interface IAppLink {
  name: string;
  tooltipContent: string;
  illustration: string;
  url: string;
  activePaths: string[];
}

export const appLinks: IAppLink[] = [
  {
    name: 'Network',
    tooltipContent: 'Explore and learn from other OpenGov customers',
    illustration: 'app-network',
    url: '/network/',
    activePaths: ['/network']
  },
  {
    name: 'Reports',
    tooltipContent: 'Create and manage reports and dashboards',
    illustration: 'app-reporting',
    url: '/reports#/',
    activePaths: ['/reports', '/reporting', '/dashboards']
  },
  {
    name: 'Budgets',
    tooltipContent: 'Create and manage budgets and proposals',
    illustration: 'app-budget',
    url: '/budgets/',
    activePaths: ['/budgets']
  },
  {
    name: 'Stories',
    tooltipContent: 'Create and manage stories (Published, Drafts and Templates)',
    illustration: 'app-stories',
    url: '/stories/',
    activePaths: ['/stories']
  },
  {
    name: 'Portal',
    tooltipContent: 'Create and manage your public transparency site',
    illustration: 'app-portal',
    url: '/portal/',
    activePaths: ['/portal']
  },
  {
    name: 'Initiatives',
    tooltipContent: 'Create and manage strategic initiatives',
    illustration: 'app-si',
    url: '/strategic-initiatives/',
    activePaths: ['/strategic-initiatives']
  },
  {
    name: 'Data',
    tooltipContent: 'Create and manage chart of accounts / upload data',
    illustration: 'app-data',
    url: '/data_manager/',
    activePaths: ['/data_manager']
  }
];

export const mapControlPanelMenuItemsToApps = (menuItems, session): IAppLink[] => {
  if (menuItems) {
    return menuItems
      .map((item) => {
        const app = appLinks.find((i) => i.name === item.name);
        return app || null;
      })
      .filter((i) => !!i);
  } else if (session && session.control_panel_menu_items)
    return session.control_panel_menu_items
      .map((item) => {
        const app = appLinks.find((i) => i.name === item.name);
        return app || null;
      })
      .filter((i) => !!i);
  else {
    return [];
  }
};

const isAppActive = (currentAppPathname: string, app: IAppLink): boolean => {
  for (const appUrl of app.activePaths) {
    if (currentAppPathname.indexOf(appUrl) > -1) {
      return true;
    }
  }

  return false;
};

export interface INavigationBarProps {
  currentAppPathname: string;
  currentEntity: string;
  menuItems?: any;
  session: any;
  showAppSwitcher?: boolean;
  activeProduct?: string;
  appName?: string;
  helpLink?: string;
}

export const DEFAULT_HELP_LINK = 'https://opengov.my.site.com/support/services/auth/sso/OpenGov_Unified_Login';

export const NavigationBar: React.FC<INavigationBarProps> = ({
  currentAppPathname,
  currentEntity,
  menuItems,
  session,
  showAppSwitcher,
  activeProduct,
  appName = 'OpenGov',
  helpLink = DEFAULT_HELP_LINK
}) => (
  <>
    <a className={styles.skipLink} href="#main">
      Skip to main content
    </a>
    <header className={styles.navigation}>
      <div className={styles.logoContainer}>
        <Illustration illustration="opengov-logo" height={40} width={40} ariaLabel="OpenGov" />
      </div>
      <div className={styles.appName}>{appName}</div>
      <div className={styles.divider} role="separator" />
      <nav className={styles.nav}>
        <ul className={cx(styles.appList, showAppSwitcher && styles.navWithScroll)}>
          {mapControlPanelMenuItemsToApps(menuItems, session).map((app) => (
            <li key={app.name}>
              <Tooltip
                className={styles.appTooltip}
                content={app.tooltipContent}
                placement="right"
                ariaLabel={app.tooltipContent}
              >
                <NavigationBarAppLink
                  isActive={isAppActive(currentAppPathname, app)}
                  name={app.name}
                  illustration={app.illustration}
                  href={`${app.url}${currentEntity}`}
                />
              </Tooltip>
            </li>
          ))}
        </ul>
        <NavigationBarBottom>
          {session && session.id && (
            <>
              {showAppSwitcher && currentEntity && (
                <AppSwitcher baseUrl={''} federatedId={currentEntity} placement="right" activeProduct={activeProduct} />
              )}
              <NavigationBarUserMenuPopover currentEntity={currentEntity} session={session} />
            </>
          )}
          <NavigationBarBottomLink href={`/entity/${currentEntity}/settings`}>Settings</NavigationBarBottomLink>
          <NavigationBarBottomLink id="walkme-navigation-help" isExternal={true} href={helpLink}>
            Help
          </NavigationBarBottomLink>
        </NavigationBarBottom>
      </nav>
    </header>
  </>
);
